/**
 * SB5-B.1 — Contracts list column definitions.
 *
 * Order locked per Christian's brief in `02-page-contracts.md` §Table:
 *   1) Contract Code  (BIGINT, sortable; default sort DESC; LEFT-aligned per SB6)
 *   2) Project Name   (string, sortable)
 *   3) Site Name      (string, sortable)
 *   4) Customer       (string, sortable)
 *   5) Contractor     (string, sortable)
 *   6) Total Assets   (int, sortable, right-aligned — numeric)
 *   7) Progress       (int 0..100 or null → em-dash, sortable)
 *   8) Status         (8-state chip)
 *   9) Edit           (pencil + "edit"; NOT sortable; click → "coming soon")
 *
 * SB6-CONS-02 column-alignment convention: text/UUID columns are LEFT,
 * numeric columns (counts, currency) are RIGHT. Contract Code displays
 * as a numeric BIGINT but reads as an identifier — keep it LEFT so the
 * column header sits at the leading edge of the row (Christian
 * [00:28:43] / [00:37:53]: *"This column has all the other columns
 * should be aligned on the left side."*).
 */
import { Box, Button, Typography } from "@mui/material";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { ContractStatusChip } from "./ContractStatusChip.js";
import { ContractProgressBar } from "./ContractProgressBar.js";
import { ownershipColumn } from "../shared/ownership/index.js";
import type { ContractListItem } from "./types.js";
import { PencilIcon } from "@aeriosiq/ui/icons";

export function buildContractColumns(args: {
  onEdit: (row: ContractListItem) => void;
  /** TNCY-6b — prepend the cross-tenant ownership tell only when the merged
   *  list actually has member rows, so an own-only tenant's list is
   *  byte-for-byte the pre-TNCY-6b table. */
  showOwnership?: boolean;
}): GridColDef<ContractListItem>[] {
  const { onEdit, showOwnership = false } = args;
  return [
    ...(showOwnership
      ? [ownershipColumn<ContractListItem>({ entityNoun: "contract" })]
      : []),
    {
      field: "contractCode",
      headerName: "Contract Code",
      sortable: true,
      flex: 0.6,
      minWidth: 120,
      // SB6-CONS-02 — text/identifier columns are left-aligned.
      align: "left",
      headerAlign: "left",
      renderCell: (p: GridRenderCellParams<ContractListItem, number>) => (
        <Box
          sx={{
            fontVariantNumeric: "tabular-nums",
            width: "100%",
            textAlign: "left",
          }}
        >
          {p.row.contractCode}
        </Box>
      ),
    },
    {
      field: "projectName",
      headerName: "Project Name",
      sortable: true,
      flex: 1.1,
      minWidth: 160,
      renderCell: (p) => (
        <Typography variant="body2" component="span">
          {p.row.projectName}
        </Typography>
      ),
    },
    {
      field: "siteName",
      headerName: "Site Name",
      sortable: true,
      flex: 1.0,
      minWidth: 140,
      valueGetter: (_v, row: ContractListItem) => row.siteName ?? "",
      renderCell: (p) => (
        <Typography variant="body2" component="span">
          {p.row.siteName ?? "—"}
        </Typography>
      ),
    },
    {
      field: "customerName",
      headerName: "Customer",
      sortable: true,
      flex: 0.9,
      minWidth: 140,
      valueGetter: (_v, row: ContractListItem) => row.customerTenantName ?? "",
      renderCell: (p) => (
        <Typography variant="body2" component="span">
          {p.row.customerTenantName ?? "—"}
        </Typography>
      ),
    },
    {
      field: "contractorName",
      headerName: "Contractor",
      sortable: true,
      flex: 0.9,
      minWidth: 140,
      valueGetter: (_v, row: ContractListItem) =>
        row.contractorTenantName ?? "",
      renderCell: (p) => (
        <Typography variant="body2" component="span">
          {p.row.contractorTenantName ?? "—"}
        </Typography>
      ),
    },
    {
      field: "totalAssets",
      headerName: "Total Assets",
      sortable: true,
      flex: 0.5,
      minWidth: 100,
      align: "right",
      headerAlign: "right",
      renderCell: (p) => (
        <Box sx={{ fontVariantNumeric: "tabular-nums" }}>
          {p.row.totalAssets}
        </Box>
      ),
    },
    {
      field: "progressPct",
      headerName: "Progress",
      sortable: true,
      flex: 0.8,
      minWidth: 140,
      renderCell: (p) => <ContractProgressBar value={p.row.progressPct} />,
    },
    {
      field: "state",
      headerName: "Status",
      sortable: true,
      flex: 0.7,
      minWidth: 130,
      renderCell: (p) => <ContractStatusChip state={p.row.state} />,
    },
    {
      field: "__edit",
      headerName: "",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      width: 96,
      align: "left",
      headerAlign: "left",
      renderCell: (p) => (
        <Button
          size="small"
          variant="text"
          startIcon={<PencilIcon fontSize="small" />}
          aria-label={`Edit contract ${String(p.row.contractCode)}`}
          onClick={(e) => {
            e.stopPropagation();
            onEdit(p.row);
          }}
        >
          Edit
        </Button>
      ),
    },
  ];
}
